import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  TOOLTIP_CONTENT_CLASSNAME,
  TOOLTIP_ARROW_CLASSNAME,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const TOOLTIP_DELAY_MS = 400;

interface MultiSelectProps<T> {
  items: T[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  getItemValue: (item: T) => string;
  getItemLabel: (item: T) => string;
  getItemDescription?: (item: T) => string;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  buttonClassName?: string;
  singularLabel?: string;
  pluralLabel?: string;
  disabled?: boolean;
}

export function MultiSelect<T>({
  items,
  selectedValues,
  onSelectionChange,
  getItemValue,
  getItemLabel,
  getItemDescription,
  placeholder = 'Select items...',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No items found.',
  buttonClassName,
  singularLabel = 'item',
  pluralLabel = 'items',
  disabled = false,
}: MultiSelectProps<T>): ReturnType<FC> {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [hoveredDescription, setHoveredDescription] = useState<string | null>(
    null,
  );
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  // Clear pending hover timer on unmount
  useEffect(() => {
    return () => clearHoverTimer();
  }, [clearHoverTimer]);

  // Filter items based on search (case-insensitive contains)
  const filteredItems = useMemo(
    () =>
      items.filter((item) =>
        getItemLabel(item).toLowerCase().includes(search.toLowerCase()),
      ),
    [items, search, getItemLabel],
  );

  const handleSelect = (item: T) => {
    const itemValue = getItemValue(item);
    const isSelected = selectedValues.includes(itemValue);
    const newSelections = isSelected
      ? selectedValues.filter((value) => value !== itemValue)
      : [...selectedValues, itemValue];
    onSelectionChange(newSelections);
  };

  const getButtonText = () => {
    if (selectedValues.length === 0) {
      return placeholder;
    }
    if (selectedValues.length === 1) {
      return `1 ${singularLabel} selected`;
    }
    return `${selectedValues.length} ${pluralLabel} selected`;
  };

  return (
    <Popover
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
          setSearch('');
          clearHoverTimer();
          setHoveredDescription(null);
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          className={cn(
            'h-9 min-h-9 w-full justify-between py-0 font-normal shadow-xs',
            'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
            buttonClassName,
          )}
          disabled={disabled}
        >
          <span className='truncate'>{getButtonText()}</span>
          <ChevronsUpDown className='ml-2 size-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='p-0'
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {filteredItems.map((item) => {
                const itemValue = getItemValue(item);
                const itemLabel = getItemLabel(item);
                const isSelected = selectedValues.includes(itemValue);
                const description = getItemDescription?.(item);

                return (
                  <CommandItem
                    key={itemValue}
                    value={itemLabel}
                    onSelect={() => handleSelect(item)}
                    className='min-w-0'
                    onMouseEnter={(e) => {
                      if (description) {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const pos = {
                          top: rect.top + rect.height / 2,
                          left: rect.left,
                        };
                        clearHoverTimer();
                        hoverTimerRef.current = setTimeout(() => {
                          setTooltipPos(pos);
                          setHoveredDescription(description);
                        }, TOOLTIP_DELAY_MS);
                      }
                    }}
                    onMouseLeave={() => {
                      clearHoverTimer();
                      setHoveredDescription(null);
                    }}
                  >
                    <Check
                      className={cn(
                        'mr-2 size-4',
                        isSelected ? 'opacity-100' : 'opacity-0',
                      )}
                    />
                    <span className='truncate'>{itemLabel}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
      {hoveredDescription && (
        <div
          role='tooltip'
          className={cn(
            TOOLTIP_CONTENT_CLASSNAME,
            'fixed max-w-xs text-xs !animate-none',
          )}
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            transform: 'translate(calc(-100% - 8px), -50%)',
          }}
        >
          <p>{hoveredDescription}</p>
          <div
            className={cn(
              TOOLTIP_ARROW_CLASSNAME,
              'absolute top-1/2 right-0 -translate-y-1/2 translate-x-[calc(50%-2px)]',
            )}
          />
        </div>
      )}
    </Popover>
  );
}
